import { createFileRoute, redirect } from '@tanstack/react-router';
import { isNil } from 'lodash-es';

import { type AppAsset } from 'api/types/AppAsset';

import { preloadCollection } from 'hooks/useQuery/useQueryCollection';
import { preloadImageLibrary } from 'hooks/useQuery/useQueryImageLibrary';
import { preloadSourceApps } from 'hooks/useQuery/useQuerySourceApps';
import { preloadVideoLibrary } from 'hooks/useQuery/useQueryVideoLibrary';

import { LibraryView } from 'views/LibraryView';

// NOTE: I don't know why this is necessary, but sometimes the search object an object with single-character string values

export interface LibrarySearch {
  type: AppAsset['type'];
  id: string;
}

const supportedTypes = ['collection', 'videoLibrary', 'imageLibrary'] as const;

export const Route = createFileRoute('/_auth/_buttonView/_layout/resources/_type/$type/_id/$id/library/')({
  component: LibraryView,
  staticData: {
    disablePointBar: true,
  },

  validateSearch: (search: Record<string, unknown>): Partial<LibrarySearch> => {
    const isValidType = supportedTypes.some((t) => {
      return t === search.type;
    });

    if (!isValidType) {
      return { type: undefined, id: undefined };
    }

    return {
      type: (search.type as AppAsset['type']) || undefined,
      id: (search.id as string) || undefined,
    };
  },

  beforeLoad: ({ params, context, search }) => {
    if (isNil((search as { type: AppAsset['type'] })?.type)) {
      throw redirect({ to: '/resources/$type/$id/', params });
    }

    context.back = {
      to: '/resources/$type/$id/',
      params,
    };

    context.disableButtonBar = true;

    return context;
  },

  loader: async ({ context, params, location }) => {
    const { queryClient } = context;

    context.disableButtonBar = true;

    const search = location.search as LibrarySearch;

    switch (search.type) {
      case 'collection':
        await preloadCollection(queryClient, params.id);
        break;

      case 'videoLibrary':
        await preloadVideoLibrary(queryClient, params.id);
        break;

      case 'imageLibrary':
        await preloadImageLibrary(queryClient, params.id);
        break;

      default:
        break;
    }

    const apps = await preloadSourceApps(queryClient);

    const app = apps.find((app) => {
      return app.id === params.id;
    });

    if (app === undefined) {
      console.error(new Error(`App not found: ${params.id}`));
    }

    context.title = app?.nameOnDevice ?? 'Unknown';

    return { app };
  },
});
